import { CHAT_MAX_LENGTH_CAP, EMOTES } from "@zend/content";

export type Segment =
  | { kind: "text"; text: string }
  | { kind: "mention"; name: string }
  | { kind: "coords"; x: number; y: number }
  | { kind: "emote"; id: string; glyph: string };

const TOKEN = /(@[A-Za-z0-9_]{3,20})|(:[a-z]{2,12}:)|(\b\d{1,3},\d{1,3}\b)/g;

/** Splits a message into text, `@mentions`, `:emotes:` (known ids only) and `x,y` coordinate links (R71). */
export function parseMessage(text: string): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    const idx = m.index ?? 0;
    const token = m[0];
    let seg: Segment | null = null;
    if (m[1]) seg = { kind: "mention", name: token.slice(1) };
    else if (m[2]) {
      const id = token.slice(1, -1);
      const emote = EMOTES.find((e) => e.id === id);
      if (emote) seg = { kind: "emote", id, glyph: emote.glyph };
    } else if (m[3]) {
      const [xs, ys] = token.split(",");
      const x = Number(xs);
      const y = Number(ys);
      if (x < 300 && y < 300) seg = { kind: "coords", x, y };
    }
    if (!seg) continue;
    if (idx > last) out.push({ kind: "text", text: text.slice(last, idx) });
    out.push(seg);
    last = idx + token.length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

export function mentionsOf(text: string): string[] {
  return [...new Set(parseMessage(text).flatMap((s) => (s.kind === "mention" ? [s.name.toLowerCase()] : [])))];
}

/** Masks listed words (whole words, case-insensitive, basic leet) with asterisks; returns the hit count. */
export function filterProfanity(text: string, words: readonly string[]): { text: string; hits: number } {
  if (words.length === 0) return { text, hits: 0 };
  let hits = 0;
  const normalise = (w: string) => w.toLowerCase().replace(/[@4]/g, "a").replace(/[3]/g, "e").replace(/[1!]/g, "i").replace(/[0]/g, "o").replace(/[5$]/g, "s");
  const set = new Set(words.map((w) => normalise(w)));
  const out = text.replace(/[A-Za-z0-9@$!ÄÖÜäöüß]+/g, (word) => {
    if (set.has(normalise(word))) {
      hits++;
      return word[0] + "*".repeat(Math.max(1, word.length - 1));
    }
    return word;
  });
  return { text: out, hits };
}

/** Message length allowed by the Comms Tower attribute (120 at level 1, +6 per level, capped at 300). */
export function messageCap(messageLengthAttribute: number): number {
  return Math.max(1, Math.min(CHAT_MAX_LENGTH_CAP, Math.round(messageLengthAttribute || 120)));
}

/** Emote ids usable at a Comms Tower level. */
export function emotesFor(commsLevel: number, equippedPacks: readonly string[] = []): string[] {
  return EMOTES.filter((e) => e.pack ? equippedPacks.includes(e.pack) : e.unlock <= Math.max(1, commsLevel)).map((e) => e.id);
}

/** Deterministic private channel id for two accounts. */
export function dmChannel(a: string, b: string): string {
  return a < b ? `dm:${a}:${b}` : `dm:${b}:${a}`;
}
